import { effect, inject, Injectable, Injector, NgZone, untracked } from '@angular/core';
import { Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { ConnectivityService, JwtService } from '@okcargo-apps/shared-infrastructure';
import { distinctUntilChanged } from 'rxjs';
import { AccountService } from '../account/account.service';

/** Interval of the periodic data refresh. */
const REFRESH_DATA_INTERVAL_MS = 300_000; // 5 min

/**
 * What each app must provide for the session lifecycle to work. They are not all the same on purpose:
 * docs-app relies on the resources that react to the account, so it does not refresh when the session
 * is verified, and only carriers-app registers a push token.
 */
export interface SessionLifecycleHooks {
  /** Refreshes the app data (used by the periodic refresh). */
  refreshAll(): void;
  /** Runs the refresh that was deferred while offline, if any. */
  refreshPendingData(): void;
  /** Called when a verified session is recovered. */
  onSessionVerified?(): void;
  /** Registers the push token, when the app has push notifications. */
  registerPushToken?(): void;
}

/**
 * @description
 * Keeps the app in sync with the session: periodic data refresh while authenticated, leaving the app
 * when a verified session is lost, and resolving what could not be done offline (the deferred refresh
 * and the translations) once the connection is back.
 *
 * It is shared by the three apps because this behaviour used to live, copy-pasted, in the three app
 * components: every fix had to be made three times.
 */
@Injectable({
  providedIn: 'root',
})
export class SessionLifecycleService {
  private readonly jwtService = inject(JwtService);
  private readonly accountService = inject(AccountService);
  private readonly connectivity = inject(ConnectivityService);
  private readonly translate = inject(TranslateService);
  private readonly router = inject(Router);
  private readonly zone = inject(NgZone);
  private readonly injector = inject(Injector);

  private hooks?: SessionLifecycleHooks;
  private refreshIntervalId?: ReturnType<typeof setInterval>;

  /** Starts watching the session and the connection. Called once, from the app component. */
  public start(hooks: SessionLifecycleHooks): void {
    this.hooks = hooks;

    // The session state is emitted as soon as it is subscribed, and it is `false` while the stored
    // session is not verified yet (the account snapshot already says that the user is authenticated).
    // Leaving the app on that initial value reloaded it over and over, so it only happens when a
    // verified session is really lost.
    let sessionWasVerified = false;

    this.jwtService.userDataReceived$.pipe(distinctUntilChanged()).subscribe((isAuthenticated) => {
      const sessionWasLost = sessionWasVerified && !isAuthenticated;
      sessionWasVerified = isAuthenticated;

      clearInterval(this.refreshIntervalId);

      if (isAuthenticated && this.accountService.isAuthenticated()) {
        this.hooks?.onSessionVerified?.();

        if (this.connectivity.isOnline()) {
          this.hooks?.registerPushToken?.();
        }
      } else if (sessionWasLost) {
        this.leaveApp();
      }

      this.zone.runOutsideAngular(() => {
        this.refreshIntervalId = setInterval(() => {
          // The timer keeps running while offline: the refresh service defers the refresh instead.
          if (this.accountService.isAuthenticated()) {
            this.zone.run(() => this.hooks?.refreshAll());
          }
        }, REFRESH_DATA_INTERVAL_MS);
      });
    });

    // What could not be done while offline is resolved once the connection is back: the deferred
    // refresh and the translations, whose assets are cached on demand and may have failed to load.
    // untracked is required: they read and write signals, so tracking them here would make this
    // effect re-run on every refresh (infinite loop).
    effect(
      () => {
        if (this.connectivity.reconnected() > 0 && this.accountService.isAuthenticated()) {
          untracked(() =>
            this.zone.run(() => {
              this.hooks?.refreshPendingData();
              this.reloadTranslations();
            }),
          );
        }
      },
      { injector: this.injector },
    );
  }

  /** Stops the periodic refresh. Called from the app component when it is destroyed. */
  public stop(): void {
    clearInterval(this.refreshIntervalId);
    this.refreshIntervalId = undefined;
  }

  /**
   * Reloads the current language, so the texts arrive when the assets could not be loaded offline.
   *
   * `use()` (not `reloadLang()`) on purpose: `reloadLang()` discards the loaded translations before
   * loading them again, so a failed load would leave the app **without any text**. `use()` is a no-op
   * when the language is already loaded and retries the load when it is not.
   */
  private reloadTranslations(): void {
    const lang = this.translate.getCurrentLang() || this.translate.getFallbackLang();

    if (lang) {
      this.translate.use(lang);
    }
  }

  /**
   * Leaves the app and its history: the reload also discards the in-memory state and the back button
   * cannot return to the authenticated screens.
   */
  private leaveApp(): void {
    this.router.navigate(['/login'], { replaceUrl: true }).then(() => window.location.replace('/login'));
  }
}
